import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';
import { FormattedMessage, useIntl } from 'react-intl';

import { Link } from '@sorare/routing';
import { StepState } from '@sorare/core/src/__generated__/globalTypes.ts';
import { Refresh } from '@sorare/core/src/atoms/icons/Refresh.tsx';
import { ConditionalWrapper } from '@sorare/core/src/atoms/layout/ConditionalWrapper/index.tsx';
import { Vertical } from '@sorare/core/src/atoms/layout/flex/index.tsx';
import { Tooltip } from '@sorare/core/src/atoms/tooltip/Tooltip/index.tsx';
import { BrandS, BrandXXS } from '@sorare/core/src/atoms/typography/index.tsx';
import { ArrowDownShape } from '@sorare/core/src/atoms/ui/ArrowDownShape/index.tsx';
import { RewardConfigImage } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigImage/index.tsx';
import { RewardConfigText } from '@sorare/core/src/components/rewards/AnyRewardConfig/RewardConfigText/index.tsx';
import { ANY_SPORT_SERIES_SQUAD_LINEUPS_STEPID } from '@sorare/core/src/constants/__generated__/routes.ts';
import { useSportContext } from '@sorare/core/src/contexts/sport/index.ts';
import { isTypeFn } from '@sorare/core/src/lib/gql/index.ts';

import { CheckmarkTile } from '#src/components/series/SeriesBoard/HotStreakTile/CheckmarkTile/index.tsx';
import { ClaimableTile } from '#src/components/series/SeriesBoard/HotStreakTile/ClaimableTile/index.tsx';
import { FailedTile } from '#src/components/series/SeriesBoard/HotStreakTile/FailedTile/index.tsx';
import { LockedTile } from '#src/components/series/SeriesBoard/HotStreakTile/LockedTile/index.tsx';
import { ReadyAndSelectedTile } from '#src/components/series/SeriesBoard/HotStreakTile/ReadyAndSelectedTile/index.tsx';
import {
  ReadyIcon,
  ReadyIconLocked,
  ReadyIconWrapper,
} from '#src/components/series/SeriesBoard/HotStreakTile/ReadyIcon/index.tsx';
import { ReadyTile } from '#src/components/series/SeriesBoard/HotStreakTile/ReadyTile/index.tsx';
import { TooltipContent } from '#src/components/series/SeriesBoard/HotStreakTile/TooltipContent/index.tsx';
import lockedTile from '#src/components/series/SeriesBoard/HotStreakTile/assets/lockedTile.webp';
import {
  CroppedPlayerImage,
  greenOverlay,
  LiveChip,
  orangeOverlay,
  TileWrapper,
  Dot,
} from '#src/components/series/SeriesBoard/HotStreakTile/ui.tsx';

import type { SquadStepTile_squadStep } from './__generated__/index.graphql.ts';

type Props = {
  step: SquadStepTile_squadStep;
  isCurrentStep: boolean;
};
export const SquadStepTile = ({ step, isCurrentStep }: Props) => {
  const { formatMessage } = useIntl();
  const { generateSportContextPath } = useSportContext();
  const { state, id, target, rewardConfigs, myLineups, acknowledged } = step;
  const firstTaskLineup = myLineups.filter(isTypeFn('TaskLineup')).at(0);
  const displayedPlayer = firstTaskLineup?.taskAppearances.at(0)?.anyPlayer;

  const lineupsUrl = generateSportContextPath(
    ANY_SPORT_SERIES_SQUAD_LINEUPS_STEPID,
    {
      params: {
        stepId: id,
      },
    }
  );

  if (!acknowledged && !isCurrentStep) {
    return (
      <TileWrapper>
        <img
          src={lockedTile}
          alt={formatMessage({ defaultMessage: 'Locked tile' })}
          width={30}
        />
      </TileWrapper>
    );
  }

  if (state === StepState.PLAYABLE) {
    return (
      <Link to={lineupsUrl}>
        <TileWrapper>
          <ReadyTile />
          <ReadyIconWrapper className="animated">
            <Tooltip
              open
              placement="top"
              showInPortal={false}
              title={
                <TooltipContent
                  target={target}
                  showTargetValue={true}
                  hasReducedThreshold={false}
                  oldThreshold={null}
                  rewardConfig={rewardConfigs.at(0)}
                />
              }
            >
              <ReadyIcon />
            </Tooltip>
          </ReadyIconWrapper>
        </TileWrapper>
      </Link>
    );
  }

  if (state === StepState.LOCKED || state === StepState.PRE_MATCHDAY_LOCKED) {
    return (
      <TileWrapper>
        <LockedTile />
        <ReadyIconWrapper>
          <ReadyIconLocked />
        </ReadyIconWrapper>
      </TileWrapper>
    );
  }

  if (state === StepState.LINEUP_SET || state === StepState.LIVE) {
    return (
      <Link to={lineupsUrl}>
        <TileWrapper>
          <ReadyAndSelectedTile />
          <ArrowDownShape
            animatedOnHover
            colors={{ start: '#FEF314', end: '#FF4E22' }}
          >
            <CroppedPlayerImage
              style={{
                background: `${orangeOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
              }}
            />
            {state === StepState.LIVE && (
              <LiveChip color="black">
                <Dot />
                <BrandXXS>
                  <FormattedMessage defaultMessage="Live" />
                </BrandXXS>
              </LiveChip>
            )}
          </ArrowDownShape>
        </TileWrapper>
      </Link>
    );
  }

  if (state === StepState.CLAIMABLE) {
    const firstRewardConfig = rewardConfigs.at(0);
    return (
      <Link
        to={generateSportContextPath(ANY_SPORT_SERIES_SQUAD_LINEUPS_STEPID, {
          params: {
            stepId: id,
          },
        })}
      >
        <TileWrapper>
          <ClaimableTile />
          <ArrowDownShape floating>
            {!!firstRewardConfig && (
              <Vertical gap={0} center>
                <RewardConfigImage anyRewardConfig={firstRewardConfig} />
                <BrandS>
                  <RewardConfigText anyRewardConfig={firstRewardConfig} />
                </BrandS>
              </Vertical>
            )}
          </ArrowDownShape>
        </TileWrapper>
      </Link>
    );
  }

  if (state === StepState.FAILED) {
    return (
      <Link to={lineupsUrl}>
        <TileWrapper>
          <FailedTile />
          <ArrowDownShape colors={{ start: '#721717', end: '#D81616' }}>
            <Vertical center>
              <Refresh />
              <BrandS>
                <FormattedMessage defaultMessage="Miss" />
              </BrandS>
            </Vertical>
          </ArrowDownShape>
        </TileWrapper>
      </Link>
    );
  }

  if (state === StepState.CLAIMED) {
    return (
      <ConditionalWrapper
        wrap={!!myLineups.length}
        Wrapper={Link}
        props={{
          to: lineupsUrl,
        }}
      >
        <TileWrapper>
          <CheckmarkTile />
          {!!myLineups.length && (
            <ArrowDownShape colors={{ start: '#00F34D', end: '#00D5FF' }}>
              <CroppedPlayerImage
                style={{
                  background: `${greenOverlay}, url(${displayedPlayer?.squaredPictureUrl}) no-repeat center / contain`,
                }}
              />
            </ArrowDownShape>
          )}
        </TileWrapper>
      </ConditionalWrapper>
    );
  }

  return null;
};

SquadStepTile.fragments = {
  squadStep: gql`
    fragment SquadStepTile_squadStep on SquadStep {
      id
      state
      target
      acknowledged
      myLineups {
        id
        ... on TaskLineup {
          id
          taskAppearances {
            id
            anyPlayer {
              slug
              squaredPictureUrl
            }
          }
        }
      }
      rewardConfigs {
        id
        ...RewardConfigImage_anyRewardConfigInterface
        ...RewardConfigText_anyRewardConfigInterface
        ...TooltipContent_anyRewardConfigInterface
      }
    }
    ${RewardConfigImage.fragments.anyRewardConfigInterface}
    ${RewardConfigText.fragments.anyRewardConfigInterface}
    ${TooltipContent.fragments.anyRewardConfigInterface}
  ` as TypedDocumentNode<SquadStepTile_squadStep>,
};
